/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 23-09-2026
 *
 * Het bouwscherm: de topbalk, het modelcanvas met alles wat erover ligt, en het simulatiescherm met de
 * splitter ertussen. De ng21-opvolger van `app/states/canvas/canvas.html` en de drie
 * AngularJS-controllers eromheen (golf 4e).
 *
 * De zeventien eilanden zijn hier gewone componenten. Vijf van hen krijgen nog iets mee: de sim-toolbar
 * twee modelwaarden, de canvasknoppen hun view en zoomgrenzen, de normbalk of hij getoond wordt, en de
 * feedbackbalk en de modeltitel hun debugvlag. De rest leest zijn eigen services.
 *
 * De stijlen staan bewust **zonder encapsulation**: `canvas.less` stijlde de DOM van de eilanden mee (de
 * knoppen van de twee toolbars), en met de gewone emulated encapsulation komen regels van de ouder daar
 * niet binnen. Alles staat genest onder `#build`, dus de facto gescoopt.
 */
import {ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, ViewEncapsulation} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslatePipe} from '@ngx-translate/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {DlModel} from '../../../app/model/dl-model';
import {ModelbeheerService} from '../../../app/model/modelbeheer.service';
import {GlobalModelcanvasService} from '../../../app/model/global-modelcanvas.service';
import {NormService} from '../../../app/engines/norm.service';
import {OpslagService} from '../../../app/model/opslag.service';
import {SamenwerkprotocolService} from '../../../app/samenwerking/samenwerkprotocol.service';
import {SimulatieService} from '../../../app/simulatie/simulatie.service';
import {VideoStatusService} from '../../../services/video-status.service';
import {CondtagService} from '../../../services/condtag.service';
import {ProjectVlaggenService} from '../../../services/project-vlaggen.service';
import {SchermindelingService} from '../../../services/schermindeling.service';
import {SessieService} from '../../../services/sessie.service';
import {SimulatieStatusService} from '../../../services/simulatie-status.service';
import {VlaggenService} from '../../../services/vlaggen.service';
import {WeergaveInstellingenService} from '../../../services/weergave-instellingen.service';
import {DlSleepbaarDirective} from '../../../directives/dl-sleepbaar/dl-sleepbaar.directive';
import {DlSplitterComponent} from '../../dl-splitter/dl-splitter.component';
import {DlModelCanvasComponent} from '../../canvas/dl-modelcanvas/dl-modelcanvas.component';
import {DlSimCanvasComponent} from '../../canvas/dl-simcanvas/dl-simcanvas.component';
import {DlModelToolbarComponent} from '../../dl-model-toolbar/dl-model-toolbar.component';
import {DlSimToolbarComponent} from '../../dl-sim-toolbar/dl-sim-toolbar.component';
import {DlModelTitleComponent} from '../../dl-model-title/dl-model-title.component';
import {DlCanvasknoppenComponent} from '../../dl-canvasknoppen/dl-canvasknoppen.component';
import {DlCondtagpaneelComponent} from '../../dl-condtagpaneel/dl-condtagpaneel.component';
import {DlFeedbackbalkComponent} from '../../dl-feedbackbalk/dl-feedbackbalk.component';
import {DlNormbalkComponent} from '../../dl-normbalk/dl-normbalk.component';
import {DlReplaybalkComponent} from '../../dl-replaybalk/dl-replaybalk.component';
import {DlChatComponent} from '../../dl-chat/dl-chat.component';
import {DlVideoComponent} from '../../dl-video/dl-video.component';
import {DlToastComponent} from '../../dl-toast/dl-toast.component';
import {DlVerbindingsstatusComponent} from '../../dl-verbindingsstatus/dl-verbindingsstatus.component';
import {DlDashboardv1Component} from '../../dl-dashboardv1/dl-dashboardv1.component';
import {DlDashboardv2Component} from '../../dl-dashboardv2/dl-dashboardv2.component';
import {TekstBeschrijvingComponent} from '../../tekst-beschrijving/tekst-beschrijving.component';

@Component({
    selector: 'dl-build',
    templateUrl: './dl-build.component.html',
    styleUrls: ['./dl-build.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslatePipe, DlSleepbaarDirective, DlSplitterComponent,
        DlModelCanvasComponent, DlSimCanvasComponent, DlModelToolbarComponent, DlSimToolbarComponent,
        DlModelTitleComponent, DlCanvasknoppenComponent, DlCondtagpaneelComponent, DlFeedbackbalkComponent,
        DlNormbalkComponent, DlReplaybalkComponent, DlChatComponent, DlVideoComponent, DlToastComponent,
        DlVerbindingsstatusComponent, DlDashboardv1Component, DlDashboardv2Component,
        TekstBeschrijvingComponent]
})
export class DlBuildComponent implements OnInit {

    /** Het document: het modelleerniveau, de readonly-stand en het snelste pad staan erin. */
    protected readonly dlModel = inject(DlModel);

    /** Het modelbeheer: de opstart van dit scherm. */
    private readonly modelbeheer = inject(ModelbeheerService);

    /** Of er iets op het modelcanvas staat; de tekstbeschrijving hangt eraan. */
    private readonly globalModelcanvas = inject(GlobalModelcanvasService);

    /** De conditieset: met een actieve set zijn de tekentools niet aan de orde. */
    private readonly condtag = inject(CondtagService);

    /** De normmatch: zonder normmatch is het naar de norm schuiven niet aan de orde. */
    private readonly norm = inject(NormService);

    /** De opslagketen, voor de openingslogregel en de debugregel. */
    private readonly opslag = inject(OpslagService);

    /** Of er gesimuleerd wordt: dan is het scherm in twee gedeeld. */
    protected readonly simulatieStatus = inject(SimulatieStatusService);

    /** De verdeling tussen de twee schermen. */
    protected readonly schermindeling = inject(SchermindelingService);

    /** De projectvlaggen: de normbalk en de beginnersmodus. */
    protected readonly projectVlaggen = inject(ProjectVlaggenService);

    /** De weergave-instellingen: de beginnersmodus en de tekstbeschrijving. */
    protected readonly weergaveInstellingen = inject(WeergaveInstellingenService);

    /** De ontwikkelvlaggen uit de localconfig. */
    protected readonly vlaggen = inject(VlaggenService);

    /** De sessie: de gebruikersnaam voor de debugregel. */
    private readonly sessie = inject(SessieService);

    /** De samenwerk-baas: de twee knoppen rechtsonder in het simulatiescherm gaan erlangs. */
    private readonly samenwerkprotocol = inject(SamenwerkprotocolService);

    /** De simulatie: die staat bij het openen van een vers bouwscherm uit. */
    private readonly simulatie = inject(SimulatieService);

    /** De instructievideo: zonder videoknop en zonder tekentools is het blok linksboven leeg. */
    private readonly videoStatus = inject(VideoStatusService);

    /**
     * De route: `actie` en `arg` komen als queryparams uit een `#/actie/<actie>/<arg>`-url. Een tweede
     * actie in een tab die al openstaat komt hier ook langs — dat gebeurt als de oude admin met
     * `target="dynaladmin"` een tweede model opent (testronde 4c, test 26).
     */
    private readonly route = inject(ActivatedRoute);

    /** De router: de actie gaat na de opstart weer uit de adresbalk. */
    private readonly router = inject(Router);

    /** Voor het afmelden van het route-abonnement. */
    private readonly opruimen = inject(DestroyRef);

    /** Of de opstart al gedaan is; een tweede actie op dezelfde route bewaart eerst het model. */
    private opgestart = false;

    /**
     * Of de tekstbeschrijving in beeld staat: de instelling én een niet-leeg model.
     */
    protected readonly tekstbeschrijvingAan = computed(() =>
        this.weergaveInstellingen.tekstbeschrijvingAan() && !this.globalModelcanvas.leeg());

    /**
     * Of het tekentoolblok er staat. Het heeft een eigen achtergrond, dus het hoort weg zodra er niets
     * in te zien is. Twee gevallen:
     *
     *  - er mag niet gewerkt worden (een replay, of de verbinding is weg) — in `canvas.html` deed de
     *    `ng-show` op de `<ul>` dat;
     *  - er is een conditieset gekozen, waardoor de drie tekentools verdwijnen, en het project heeft
     *    geen instructievideo, waardoor ook het `dl-video`-eiland zichzelf verbergt. Dan bleef er een
     *    grijs vierkantje van 10×10 over: de `padding: 5px` van een lege lijst. Dat staat sinds
     *    05-09-2026 als los eindje in `migratieplan.md`, belegd bij deze golf.
     */
    protected readonly tekentoolsblokAan = computed(() =>
        !this.dlModel.fullReadonly && (!this.condtag.condtag() || this.videoStatus.magVideo()));

    /**
     * Of de tekentools er staan: niet in readonly, en niet terwijl er een conditieset gekozen is.
     */
    protected readonly tekentoolsAan = computed(() => !this.dlModel.fullReadonly && !this.condtag.condtag());

    /**
     * Of de knop voor de beginnersmodus er staat: het project kent de modus én er mag gewerkt worden —
     * de toggle slaat de voorkeur op bij het backend, en in readonly hoort er niets die kant op te gaan.
     */
    protected readonly magBeginnersmodusToggle = computed(() =>
        this.projectVlaggen.beginnersmodus() && !this.dlModel.fullReadonly);

    /**
     * Of de knop "schuif naar de norm" er staat: er is een normmatch, er wordt niet gesimuleerd en er
     * mag gewerkt worden. Een replaystap draagt lege mappings, waar snapNorm juist uit leest.
     */
    protected readonly toonSnapnorm = computed(() =>
        !!this.norm.normmatch() && !this.simulatieStatus.simuleren() && !this.dlModel.fullReadonly);

    /**
     * De regel linksonder met de gebruikersnaam en het model-id; alleen zichtbaar bij het zweven
     * erover, en alleen leesbaar voor wie weet dat hij er staat.
     */
    protected readonly debuginfo = computed(() => {
        const id = this.opslag.modelId();
        const notifyId = this.opslag.notifyId();
        const eigen = notifyId && notifyId !== id ? ` == ${notifyId}` : '';
        return `${this.sessie.naam() || '?'} ${id ?? '?'}${eigen}`;
    });

    ngOnInit(): void {
        //een vers bouwscherm begint zonder conditieset en met een dicht paneel, ook als het vorige ze had
        this.condtag.reset();
        this.volgDeRoute();
    }

    /**
     * Zet de beginnersmodus om. In readonly niet: de toggle slaat de voorkeur op bij het backend.
     */
    protected toggleBeginnersmodus(): void {
        if (this.dlModel.fullReadonly) {
            return;
        }
        this.weergaveInstellingen.toggleBeginnersmodus();
    }

    /**
     * Zet de tekstbeschrijving om.
     */
    protected toggleTekstbeschrijving(): void {
        this.weergaveInstellingen.toggleTekstbeschrijving();
    }

    /**
     * Begint het plaatsen van een node vanuit de tekentools. Het canvas houdt de add-actie bij en
     * plaatst hem bij de volgende klik; de knop gaat mee zodat een tweede klik erop annuleert.
     */
    protected startAddActie(gebeurtenis: Event, type: string): void {
        this.globalModelcanvas.startAddActie(type, {type}, gebeurtenis.currentTarget as HTMLElement);
    }

    /**
     * Schuift het model naar de norm.
     */
    protected snapNorm(): void {
        this.globalModelcanvas.snapNorm();
    }

    /**
     * De twee knoppen rechtsonder in het simulatiescherm: de toestanden mooi neerleggen en ze terug naar
     * hun standaardplek zetten. Ze gaan langs de baas, die als volger een verzoek aan de leider stuurt.
     */
    protected doeSim(actie: string): void {
        this.samenwerkprotocol.doeSim(actie);
    }

    /**
     * Zet een vers, leeg model neer en doet daarna de opstart. Dat lege model is geen formaliteit: de
     * reset van de opslagketen geeft het zijn vertaalde standaardtitel ("New model") en laat
     * `bepaalModelleerNiveau()` het niveau uit de projectinstellingen halen. Zonder die stap staat er
     * geen modelnaam in de titelbalk en blijft het modelleerniveau 0, waardoor de tekentools voor agent
     * (vanaf 30) en assumption (vanaf 40) niet verschijnen.
     *
     * De simulatie gaat eerst uit. Op een verse pagina staat hij al uit, maar de shell bouwt dit scherm
     * ook opnieuw op zonder herlading: een `#/code/<projectcode>`-url logt uit en weer in, en een
     * verlopen sessie komt via de guard op het inlogscherm en daarna hier terug. Dan leven de services
     * door. Dit is de volgorde van de AngularJS-init in `cytocanvas.controller.js`.
     *
     * Haalt de opstart daarna een sjabloon, een normmodel of een quickLoad-model op, dan reset die
     * keten opnieuw en overschrijft dit; dat was ook in AngularJS zo.
     * @param [actie] de actie uit de url
     * @param [arg] het argument daarbij
     */
    private async beginOpnieuw(actie: string | null, arg: string | null): Promise<void> {
        this.simulatie.stop(true);
        await this.opslag.nieuw();
        await this.modelbeheer.startOp(actie, arg);
        this.wisActieUitUrl();
    }

    /**
     * Haalt `actie` en `arg` weer uit de adresbalk zodra de opstart ze verwerkt heeft. In AngularJS
     * stonden ze als niet-url-params op de build-state (`states.js`: `url: '/build/'` met een eigen
     * `params`-blok), dus na de doorstap stond er alleen `#!/build/`. Blijven ze hier staan, dan voert
     * elke herlading de actie opnieuw uit — en `wisselProject` en `nieuwMenu` bij `forceer_start`
     * herladen met `location.reload()`, dus dan zou een `fromAdmin`-sleutel na een projectwissel nog een
     * keer opengaan, in het verkeerde project.
     *
     * De navigatie laat het abonnement nog eens vuren met een lege `actie`; die tak doet niets.
     */
    private wisActieUitUrl(): void {
        if (!this.route.snapshot.queryParamMap.keys.length) {
            return;
        }
        void this.router.navigate([], {relativeTo: this.route, queryParams: {}, replaceUrl: true});
    }

    /**
     * Volgt de queryparams van de route. De eerste keer is dat de opstart; daarna gaat het om een actie
     * in een tab die al openstaat, en dan wordt eerst het huidige model bewaard.
     */
    private volgDeRoute(): void {
        this.route.queryParamMap.pipe(takeUntilDestroyed(this.opruimen)).subscribe(params => {
            const actie = params.get('actie');
            const arg = params.get('arg');
            if (!this.opgestart) {
                this.opgestart = true;
                //de openingslogregel, zoals `init_app` in AngularJS
                this.opslag.directlogAction('init_app', {openAction: actie, openArg: arg}, 'app', 'app', true);
                void this.beginOpnieuw(actie, arg);
                return;
            }
            if (actie) {
                void this.heropen(actie, arg);
            }
        });
    }

    /**
     * Een tweede actie in een tab die al openstaat: het huidige model wordt eerst bewaard, en daarna
     * gaat de actie weer uit de adresbalk.
     * @param actie de actie uit de url
     * @param [arg] het argument daarbij
     */
    private async heropen(actie: string, arg: string | null): Promise<void> {
        await this.modelbeheer.heropen(actie, arg);
        this.wisActieUitUrl();
    }
}
